import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { registerConfirm, unregisterConfirm } from '../utils/confirmBridge';
import { motion, AnimatePresence } from 'framer-motion';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from './LanguageContext';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const { t } = useLanguage();
  const [dialog, setDialog] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback(
    (message, options = {}) =>
      new Promise((resolve) => {
        resolveRef.current = resolve;
        setDialog({
          message,
          title: options.title ?? t('common.confirmTitle'),
          confirmLabel: options.confirmLabel ?? t('common.confirmBtn'),
          cancelLabel: options.cancelLabel ?? t('common.cancelBtn'),
          variant: options.variant ?? 'danger',
        });
      }),
    [t]
  );

  useEffect(() => {
    registerConfirm(confirm);
    return () => unregisterConfirm();
  }, [confirm]);

  const close = (result) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setDialog(null);
  };

  const confirmBtnClass =
    dialog?.variant === 'danger'
      ? 'bg-forma-600 hover:bg-forma-500 text-white shadow-md shadow-forma-600/20'
      : 'bg-forma-600 hover:bg-forma-500 text-white shadow-md shadow-forma-600/20';

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <AnimatePresence>
        {dialog && (
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
          >
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
              onClick={() => close(false)}
              aria-label={dialog.cancelLabel}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 8 }}
              transition={{ duration: 0.15 }}
              className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-soft dark:border-slate-700 dark:bg-slate-900"
            >
              <div className="mb-4 flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400">
                  <ExclamationTriangleIcon className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2
                    id="confirm-dialog-title"
                    className="text-lg font-semibold text-slate-900 dark:text-slate-50"
                  >
                    {dialog.title}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                    {dialog.message}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => close(false)}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  {dialog.cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={() => close(true)}
                  className={`rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${confirmBtnClass}`}
                >
                  {dialog.confirmLabel}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return ctx.confirm;
}
